import { useState } from 'react';
import { Button } from '@carbon/react';
import { Copy, View } from '@carbon/icons-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export type RevealSource = 'withdraw' | 'wallet_cu' | 'bank_link' | 'payout' | 'agent';

// Copy a value to the clipboard (the page is https or localhost; the textarea path covers an older browser).
export async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const t = document.createElement('textarea');
    t.value = text;
    t.style.position = 'fixed';
    t.style.opacity = '0';
    document.body.appendChild(t);
    t.select();
    document.execCommand('copy');
    t.remove();
  }
}

// A small copy button that says "Đã chép" for a moment after a copy.
export function CopyBtn({ get, disabled, label = 'Sao chép' }: { get: () => Promise<string | null> | string | null; disabled?: boolean; label?: string }) {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <span className="copy-btn">
      <Button kind="ghost" size="sm" hasIconOnly renderIcon={Copy} iconDescription={label} disabled={disabled || busy}
        onClick={async () => {
          setBusy(true);
          try {
            const v = await get();
            if (v == null) return;
            await copyText(v);
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          } finally {
            setBusy(false);
          }
        }} />
      {done ? <span className="muted" role="status"> Đã chép</span> : null}
    </span>
  );
}

// A masked bank number or phone (P1-D18). "Xem" asks the server for the full value of this one record;
// the server writes an audit row for every reveal. The full value lives only in this component.
// copy: a copy button too (rút 1 bước): it reveals the value first when needed (the same audited reveal), then copies it.
export function Reveal({ masked, source, id, field = 'soTk', copy, copyDisabled }: {
  masked: string | null | undefined; source: RevealSource; id: string | number; field?: string; copy?: boolean; copyDisabled?: boolean;
}) {
  const { can } = useAuth();
  const [full, setFull] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!masked) return <span className="muted">-</span>;
  const reveal = async (): Promise<string | null> => {
    if (full != null) return full;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ values: Record<string, string | null> }>('/api/admin/pii/reveal', { source, id: String(id) });
      const v = r.values[field] ?? '-';
      setFull(v);
      return v;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const copyBtn = copy && can('pii.reveal') ? <CopyBtn get={reveal} disabled={copyDisabled} label="Sao chép số đầy đủ" /> : null;
  // one tree for both states: the copy button keeps its place (and its "Đã chép") when the value is revealed
  return (
    <span className="reveal">
      <span className="num">{full ?? masked}</span>
      {full == null && can('pii.reveal') ? (
        <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="Xem đầy đủ" disabled={busy || (copy && copyDisabled)}
          onClick={() => { void reveal(); }} />
      ) : null}
      {copyBtn}
      {err ? <span className="num--neg"> {err}</span> : null}
    </span>
  );
}
